import React, { useRef, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { assetUploadStyles } from './AssetUpload.styles';
import { useListPropertyContext } from '../../contexts/ListPropertyContext';

const AssetUpload = () => {
  const fileInputRef = useRef(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, src: null, type: null });
  const { formData, addAsset, removeAsset } = useListPropertyContext();

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        addAsset(file, reader.result);
      };
      if (file.type.startsWith('image/')) {
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        // For videos, create a video element to get thumbnail
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          video.currentTime = 1; // Seek to 1 second
        };
        video.onseeked = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          addAsset(file, canvas.toDataURL());
        };
        video.src = URL.createObjectURL(file);
      }
    });
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreviewClick = (asset, index) => {
    if (asset.file.type.startsWith('image/')) {
      setPreviewDialog({ open: true, src: asset.preview, type: 'image' });
    } else if (asset.file.type.startsWith('video/')) {
      const videoUrl = URL.createObjectURL(asset.file);
      setPreviewDialog({ open: true, src: videoUrl, type: 'video' });
    }
  };

  const handleCloseDialog = () => {
    if (previewDialog.type === 'video' && previewDialog.src) {
      URL.revokeObjectURL(previewDialog.src);
    }
    setPreviewDialog({ open: false, src: null, type: null });
  };

  const handleRemoveAsset = (e, index) => {
    e.stopPropagation();
    removeAsset(index);
  };

  const styles = assetUploadStyles();

  return (
    <Box sx={styles.container}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      
      <Box
        sx={styles.uploadButton}
        onClick={() => fileInputRef.current?.click()}
      >
        <Typography sx={styles.plusIcon}>+</Typography>
      </Box>

      <Box sx={styles.previewGrid}>
        {formData.assets.map((asset, index) => (
          <Box
            key={index}
            sx={styles.previewItem}
            onClick={() => handlePreviewClick(asset, index)}
          >
            {asset.file.type.startsWith('image/') ? (
              <Box
                component="img"
                src={asset.preview}
                alt={`Preview ${index + 1}`}
                loading="lazy"
                sx={styles.previewImage}
              />
            ) : (
              <Box sx={styles.previewVideo}>
                <Box
                  component="img"
                  src={asset.preview}
                  alt={`Video thumbnail ${index + 1}`}
                  loading="lazy"
                  sx={styles.previewImage}
                />
                <Box sx={styles.playIconOverlay}>
                  <PlayArrowIcon sx={styles.playIcon} />
                </Box>
              </Box>
            )}
            <Box
              className="close-button"
              sx={styles.closeButton}
              onClick={(e) => handleRemoveAsset(e, index)}
            >
              <CloseIcon sx={styles.closeIcon} />
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog
        open={previewDialog.open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        PaperProps={{
          sx: styles.dialogPaper,
        }}
      >
        {previewDialog.type === 'image' && (
          <Box
            component="img"
            src={previewDialog.src}
            alt="Preview"
            sx={styles.dialogImage}
          />
        )}
        {previewDialog.type === 'video' && (
          <Box
            component="video"
            src={previewDialog.src}
            controls
            sx={styles.dialogVideo}
          />
        )}
        <IconButton
          sx={styles.dialogCloseButton}
          onClick={handleCloseDialog}
        >
          <CloseIcon />
        </IconButton>
      </Dialog>
    </Box>
  );
};

export default AssetUpload;

